import React, { useState, useEffect } from 'react';
import { ClipboardList, AlertTriangle, Clock, ChevronRight, CheckCircle, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const TriageWidget = () => {
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState({ pending: 0, overdue: 0, due_today: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchTriageData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get the current user's identifier for filtering
      const userFilter = user?.username || '';
      const assignedParam = userFilter ? `&assigned_to=${encodeURIComponent(userFilter)}` : '';

      // Fetch pending triage actions and stats in parallel, filtered by current user
      const [actionsResponse, statsResponse] = await Promise.all([
        api.get(`/triage/?status=pending&limit=10${assignedParam}`),
        api.get(`/triage/stats${assignedParam ? `?assigned_to=${encodeURIComponent(userFilter)}` : ''}`)
      ]);

      // Process actions
      const actionsArray = Array.isArray(actionsResponse.data) ? actionsResponse.data : [];
      // Sort by due date, showing overdue and due today first
      const sorted = actionsArray.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
      setActions(sorted);

      // Process stats
      if (statsResponse.data) {
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch triage data:', error);
      setError('Failed to load triage data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchTriageData();
    }
  }, [user]);

  const formatDueDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(date);
    dueDate.setHours(0, 0, 0, 0);

    const diffDays = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { text: `${Math.abs(diffDays)}d overdue`, isOverdue: true };
    if (diffDays === 0) return { text: 'Due today', isDueToday: true };
    if (diffDays === 1) return { text: 'Tomorrow', isUpcoming: true };
    if (diffDays <= 7) return { text: `${diffDays} days`, isUpcoming: true };
    return { text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), isUpcoming: false };
  };

  const getStatusColor = (dueInfo) => {
    if (dueInfo.isOverdue) return 'bg-red-500';
    if (dueInfo.isDueToday) return 'bg-amber-500';
    return 'bg-blue-500';
  };

  if (loading) {
    return (
      <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5 h-full">
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5 h-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
              <ClipboardList className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-white">Triage Queue</h2>
          </div>
        </div>
        <div className="text-center py-6">
          <p className="text-slate-400 mb-3">{error}</p>
          <button
            onClick={fetchTriageData}
            className="text-sm text-blue-300 hover:text-white transition-colors px-4 py-2 bg-blue-500/20 rounded-lg hover:bg-blue-500/30"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
            <ClipboardList className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-semibold text-white">Triage Queue · {stats.pending ?? 0}</h2>
        </div>
        <button
          onClick={() => navigate('/triage')}
          className="text-sm text-slate-400 hover:text-white flex items-center transition-colors px-3 py-1.5 hover:bg-white/5 rounded-lg"
        >
          View All
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-slate-700/30 rounded-xl p-2.5 text-center border border-slate-600/30">
          <div className="text-xl font-bold text-white">{stats.pending}</div>
          <div className="text-xs text-slate-400">Pending</div>
        </div>
        <div className="bg-red-500/10 rounded-xl p-2.5 text-center border border-red-500/20">
          <div className="text-xl font-bold text-red-400">{stats.overdue}</div>
          <div className="text-xs text-red-400">Overdue</div>
        </div>
        <div className="bg-amber-500/10 rounded-xl p-2.5 text-center border border-amber-500/20">
          <div className="text-xl font-bold text-amber-400">{stats.due_today}</div>
          <div className="text-xs text-amber-400">Due Today</div>
        </div>
      </div>

      {/* Actions List */}
      <div className="flex-1 max-h-80 overflow-y-auto pr-1">
        {actions.length === 0 ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3 border border-emerald-500/20">
              <CheckCircle className="w-7 h-7 text-emerald-400" />
            </div>
            <p className="text-slate-400 text-sm mb-3">All caught up!</p>
            <button
              onClick={() => navigate('/triage')}
              className="text-sm text-blue-300 hover:text-white transition-colors px-4 py-2 bg-blue-500/20 rounded-lg hover:bg-blue-500/30"
            >
              Create action
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {actions.map((action) => {
              const dueInfo = formatDueDate(action.due_date);
              return (
                <div
                  key={action.id}
                  onClick={() => navigate('/triage')}
                  className="flex items-start p-3 rounded-xl hover:bg-white/5 cursor-pointer transition-all border border-transparent hover:border-slate-700 group"
                >
                  {/* Status indicator */}
                  <div className={`w-1 h-full rounded-full mr-3 flex-shrink-0 ${getStatusColor(dueInfo)}`} style={{ minHeight: '2.5rem' }} />

                  <div className="flex-1 min-w-0">
                    {/* Action description */}
                    <p className="font-medium text-white text-sm truncate group-hover:text-blue-200">
                      {action.action_description}
                    </p>

                    {/* Client name - Case name */}
                    {(action.client_name || action.case_name) && (
                      <div className="flex items-center text-xs text-slate-500 mt-1">
                        <Briefcase className="w-3 h-3 mr-1 text-slate-500" />
                        <span className="truncate">
                          {action.client_name ? `${action.client_name}` : action.case_name}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Due date badge */}
                  <div className={`flex-shrink-0 ml-2 px-2 py-1 rounded-lg text-xs font-medium ${
                    dueInfo.isOverdue ? 'bg-red-500/20 text-red-400' :
                    dueInfo.isDueToday ? 'bg-amber-500/20 text-amber-400' :
                    'bg-slate-700/50 text-slate-300'
                  }`}>
                    {dueInfo.text}
                  </div>
                </div>
              );
            })}
            {actions.length > 3 && (
              <button
                onClick={() => navigate('/triage')}
                className="w-full text-center text-xs text-slate-400 hover:text-white py-2 transition-colors rounded-lg hover:bg-white/5"
              >
                +{actions.length - 3} more
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TriageWidget;
